import type { ReferenciaAltura } from './referenciasAltura'

/**
 * Redutores de força e o que acontece na ancoragem, num arquivo de dados, longe
 * da lógica. Todos os valores são PROVISÓRIOS (`confirmado: false`): são ordens
 * de grandeza didáticas, não ensaio. O instrutor confere e corrige aqui.
 *
 * Um redutor de força é um freio de força constante: o sistema segura a força
 * em `forcaKN` e escorrega até `aberturaM` enquanto a energia da queda acaba.
 * O absorvedor de energia é o primeiro caso; o Prusik e o freio dinâmico (a
 * corda que o assegurador deixa correr) são o mesmo mecanismo, com outra força
 * e outra distância. As medidas do absorvedor continuam sendo parâmetros do
 * trabalhador; as dos outros vêm daqui.
 */

export type TipoRedutor = 'absorvedor' | 'prusik' | 'freio-dinamico'

export interface DadosRedutor {
  readonly nome: string
  /** Como a frase o chama: "o absorvedor abre", "o Prusik escorrega". */
  readonly nomeCurto: string
  /** `null`: vem dos parâmetros do trabalhador (força e abertura do absorvedor). */
  readonly forcaKN: number | null
  readonly aberturaM: number | null
  readonly origem: string
  readonly confirmado: boolean
}

export const REDUTORES_FORCA: Readonly<Record<TipoRedutor, DadosRedutor>> = {
  absorvedor: {
    nome: 'absorvedor de energia',
    nomeCurto: 'absorvedor',
    forcaKN: null,
    aberturaM: null,
    origem: 'A força de frenagem e a abertura são parâmetros do trabalhador: o instrutor usa os do fabricante do equipamento.',
    confirmado: false,
  },
  prusik: {
    nome: 'Prusik na corda',
    nomeCurto: 'Prusik',
    forcaKN: 3.5,
    aberturaM: 0.6,
    origem:
      'Ordem de grandeza: o nó escorrega entre 2 kN e 5 kN conforme o cordelete, a corda e o número de voltas, e escorrega alguns decímetros. Conferir com o ensaio do instrutor.',
    confirmado: false,
  },
  'freio-dinamico': {
    nome: 'freio dinâmico (assegurador)',
    nomeCurto: 'freio dinâmico',
    forcaKN: 2.5,
    aberturaM: 1.5,
    origem:
      'Ordem de grandeza: o assegurador deixa a corda correr pelo freio e segura a força baixa por mais distância. Depende do dispositivo, da técnica e de quem assegura. Conferir com o instrutor.',
    confirmado: false,
  },
}

/** A ordem do parâmetro `redutorForca` do trabalhador (0, 1, 2). */
export const ORDEM_REDUTORES: readonly TipoRedutor[] = ['absorvedor', 'prusik', 'freio-dinamico']

export function redutorDoIndice(indice: number): TipoRedutor {
  return ORDEM_REDUTORES[Math.min(ORDEM_REDUTORES.length - 1, Math.max(0, Math.round(indice)))]!
}

export interface RedutorEfetivo {
  readonly tipo: TipoRedutor
  readonly nome: string
  readonly nomeCurto: string
  readonly forcaKN: number
  /** Abertura máxima: 0 é sem redutor. */
  readonly aberturaM: number
}

/** A força e a abertura em uso: as do trabalhador (absorvedor) ou as do Prusik e do freio dinâmico. */
export function redutorEfetivo(parametros: Readonly<Record<string, number>>): RedutorEfetivo {
  const tipo = redutorDoIndice(parametros.redutorForca ?? 0)
  const dados = REDUTORES_FORCA[tipo]
  return {
    tipo,
    nome: dados.nome,
    nomeCurto: dados.nomeCurto,
    forcaKN: dados.forcaKN ?? parametros.forcaAbsorvedorKN!,
    aberturaM: dados.aberturaM ?? parametros.absorvedorAberturaM!,
  }
}

// ---------------------------------------------------------------------------
// A corda que dobra no alto
// ---------------------------------------------------------------------------

export type DobraNoAlto = 'direta' | 'polia' | 'mosquetao'

export interface DadosDobra {
  readonly nome: string
  /** Atrito da corda na curva de 180°; 0 é sem curva. */
  readonly coeficienteAtrito: number
  readonly origem: string
  readonly confirmado: boolean
}

export const DOBRAS_NO_ALTO: Readonly<Record<DobraNoAlto, DadosDobra>> = {
  direta: {
    nome: 'talabarte direto na ancoragem',
    coeficienteAtrito: 0,
    origem: 'A ancoragem recebe só a força do corpo.',
    confirmado: true,
  },
  polia: {
    nome: 'corda por uma polia no alto',
    coeficienteAtrito: 0.05,
    origem: 'Atrito pequeno de uma roldana: a corda dobra e a ancoragem recebe os dois ramos, quase o dobro. Conferir com o fabricante.',
    confirmado: false,
  },
  mosquetao: {
    nome: 'corda por um mosquetão no alto',
    coeficienteAtrito: 0.3,
    origem: 'Atrito da corda sobre o aço do mosquetão (ordem de 0,2 a 0,3): o ramo de retorno segura menos. Conferir com o instrutor.',
    confirmado: false,
  },
}

export const ORDEM_DOBRAS: readonly DobraNoAlto[] = ['direta', 'polia', 'mosquetao']

export function dobraDoIndice(indice: number): DobraNoAlto {
  return ORDEM_DOBRAS[Math.min(ORDEM_DOBRAS.length - 1, Math.max(0, Math.round(indice)))]!
}

/**
 * Quantas vezes a força do corpo chega à ancoragem. A corda dobra 180° no alto
 * e a ponta de retorno segura a força pelo atrito da curva (Euler-Eytelwein):
 * `T_retorno = F / e^(μ·π)`, e a ancoragem recebe os dois ramos,
 * `F · (1 + e^(−μ·π))`. Sem atrito (μ → 0) chega a 2; sem dobra, é 1.
 */
export function fatorNaAncoragem(dobra: DobraNoAlto): number {
  const { coeficienteAtrito } = DOBRAS_NO_ALTO[dobra]
  if (dobra === 'direta') return 1
  return 1 + Math.exp(-coeficienteAtrito * Math.PI)
}

// ---------------------------------------------------------------------------
// O ensaio de referência
// ---------------------------------------------------------------------------

export interface EnsaioReferencia extends ReferenciaAltura {
  readonly massaKg: number
  readonly quedaLivreM: number
  readonly comprimentoCordaM: number
}

/**
 * O ensaio de referência do resgate com corda: uma massa cai uma altura num
 * comprimento de corda, e o pico de força é comparado com o limite. Os números
 * e o critério ficam aqui, provisórios, para o instrutor corrigir: o app não
 * afirma que este ensaio é o de uma norma.
 */
export const ENSAIO_REFERENCIA: EnsaioReferencia = {
  massaKg: 200,
  quedaLivreM: 1,
  comprimentoCordaM: 3,
  valor: 6,
  unidade: 'kN',
  origem:
    'Valores didáticos (200 kg caindo 1 m em 3 m de corda, critério de 6 kN no corpo). O instrutor confirma a massa, a altura, o comprimento e o critério do ensaio que a corporação adota.',
  confirmado: false,
}
